/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState
} from 'react';
import {makeStyles} from '@material-ui/styles';
import {
  Column,
  GridApi,
  RowNode
} from 'ag-grid-community';
import {AgGridReact} from 'ag-grid-react';
import classNames from 'classnames';
import {isEmpty} from 'lodash';
import {GenericObject} from '@ion/core/src/types/entities';
import {useIsPhone} from '../hooks/useMedia';
import {dlsColor} from '../theme/theme';
import {ExportClient} from './Exporter';
import {RowModelTypes} from './IonGrid';
import {
  convertFilterModel,
  convertSortModel,
  getSortModel,
  HEADER_HEIGHT,
  ROW_HEIGHT,
  SortModelType
} from './IonGridUtils';
import {IonSpinnerTemplate} from './IonSpinnerTemplate';
import {ColumnDefinitions} from './useColumnDefinitions';

interface IonInfiniteScrollGridProps {
  columnDefinitions?: ColumnDefinitions;
  onGridReady: (api: GridApi) => void;
  columnComponents?: {
    [key: string]: ({onClick, data, value}: { onClick?: Function, data?: any, value?: any }) => JSX.Element;
  };
  cacheBlockSize?: number;
  filterModelConverter?: Function;
  sortModelConverter?: Function;
  attributeMappings?: GenericObject;
  augmenter?: Function;
  defaultSort?: SortModelType | {};
  autoGroupColumnDef?: GenericObject;
  isEnableCellTextSelection?: boolean;
  customGridClass?: string;
}

const useStyles = makeStyles(() => ({
  defaultGridHeight : {
    '& .ag-cell-value' : {
      display : 'flex !important'
    },
    '& .ag-cell' : {
      display : 'flow !important'
    },
    '& .ag-center-cols-container' : {
      minWidth : '100%'
    },
    '& .ag-row-hover.ag-row-hover, .ag-row-hover, .ag-row-selected.ag-row-selected, .ag-row-selected, .ag-row-focus.ag-row-focus, .ag-row-focus' : {
      backgroundColor : `${dlsColor('paleBlue')} !important`,
      backgroundImage : 'none !important',
      '&::before'     : {
        backgroundColor : `${dlsColor('paleBlue')} !important`,
        backgroundImage : 'none !important'
      }
    },
    '& .ag-pinned-right-cols-container' : {
      '& .ag-cell-first-right-pinned' : {
        '& .MuiButtonBase-root' : {
          paddingLeft : '0px !important'
        }
      }
    },
    '& .ag-ltr' : {
      '& .ag-root-wrapper-body' : {
        '& .ag-body.ag-layout-normal' : {
          height : '280px'
        }
      },
      '& .ag-cell.ag-cell-value' : {
        paddingTop  : '0 !important',
        borderColor : '#d2d2d2 !important'
      },
      '& .ag-pinned-left-cols-container' : {
        '& .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell)' : {
          paddingLeft : '0px !important'
        },
        '& .ag-cell.ag-column-first' : {
          paddingLeft : '0px !important'
        }
      }
    },
    '& .ag-header-select-all' : {
      marginLeft : 7
    },
    '& .ag-popup-editor' : {
      boxShadow : 'none !important'
    },
    '& .ag-theme-alpine.ag-theme-alpine .ag-popup-child:not(.ag-tooltip-custom)' : {
      minWidth : '0px !important'
    },
    '& .ag-pinned-left-header' : {
      '& .ag-column-first' : {
        width   : '0px !important',
        padding : '0px'
      },
      '& .ag-header-cell' : {
        left        : '15px !important',
        paddingLeft : '1px !important'
      }
    },
    width     : '100%',
    height    : '100%',
    overflowY : 'scroll'
  }
}));

/**
 * The IonInfiniteScrollGrid component displays a responsive grid of rows.
 *
 * If a ref is forwarded to the component, the imperative handle will return export functionality
 * of the displayed row & column data.
 *
 * The custom datasource for retrieving row data can be set in the 'onGridReady' callback passed in.
 *
 * ```typescript
 *
 * const onGridReady = (api) => {
 *  const datasource = getDataSource(api, ...);
 *  api.setGridOption('serverSideDatasource', datasource);
 * };
 *
 * <IonInfiniteScrollGrid ref={taskRef}
 *   onGridReady={onGridReady}
 *   cacheBlockSize={1000}
 *   columnDefinitions={columnDefinitions}
 *   columnComponents={{...}} />
 *
 * ```
 */
export const IonInfiniteScrollGrid = forwardRef(({
  onGridReady: parentOnGridReady,
  columnDefinitions,
  columnComponents,
  attributeMappings,
  augmenter,
  autoGroupColumnDef,
  defaultSort = {},
  filterModelConverter,
  sortModelConverter,
  customGridClass,
  cacheBlockSize = 100,
  isEnableCellTextSelection = false
}: IonInfiniteScrollGridProps, ref) => {
  const classes               = useStyles();
  const isPhone               = useIsPhone();
  const [gridApi, setGridApi] = useState<GridApi>();

  const onGridReady = useCallback(({api}) => {
    setGridApi(api);
    parentOnGridReady?.(api);
  }, []);

  const colDefs = isPhone ? columnDefinitions?.mobile : columnDefinitions?.desktop;

  const getFilters = useCallback((filter, modularSearchFilter) => {
    let filterOn = modularSearchFilter;
    if (isEmpty(filterOn)) {
      filterOn = filterModelConverter?.(filter, attributeMappings);
      if (!filterOn) {
        filterOn = convertFilterModel(filter, attributeMappings);
      }
    }
    return filterOn;
  }, [attributeMappings, filterModelConverter]);

  const getItems = useCallback((enableSelectionsExport) => {
    let items = [];
    if (enableSelectionsExport) {
      items = gridApi.getSelectedNodes().map((node: RowNode) => node.data);
    }
    return items;
  }, [gridApi]);

  const getSort = useCallback((sort) => {
    const sortConverter = sortModelConverter || convertSortModel;
    return sortConverter(sort, attributeMappings);
  }, [attributeMappings, sortModelConverter]);

  useImperativeHandle(ref, () => {
    return {
      /**
       * Defines the export parameters function which returns an object containing
       * the grids current state for use in subsequent exporting of the grid.
       */
      getExportParams : (
        includeSubGroups: boolean = false,
        enableSelectionsExport: boolean = false,
        modularSearchFilter: GenericObject = {}
      ): ExportClient => {
        if (gridApi) {
          const {sort, filter} = getParams(gridApi, defaultSort);
          return {
            filterOn : getFilters(filter, modularSearchFilter),
            items    : getItems(enableSelectionsExport),
            columns  : getColumns(gridApi),
            sortOn   : getSort(sort),
            augmenter,
            includeSubGroups
          };
        }
      }
    };
  }, [gridApi]);

  return (
    <div className={classNames('ag-theme-quartz', classes.defaultGridHeight, customGridClass)}>
      <AgGridReact animateRows suppressMenuHide suppressAggFuncInHeader
        components={columnComponents}
        cacheBlockSize={cacheBlockSize}
        columnDefs={colDefs}
        autoGroupColumnDef={autoGroupColumnDef}
        overlayLoadingTemplate={IonSpinnerTemplate}
        rowModelType={RowModelTypes.ServerSide}
        onGridReady={onGridReady}
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        enableCellTextSelection={isEnableCellTextSelection} />
    </div>
  );
});

const getParams = (api, defaultSort) => {
  const sort = getSortModel(api);
  return {
    sort   : sort.length ? sort : [defaultSort],
    filter : api.getFilterModel()
  };
};

const getColumns = (api) => (
  api.getAllDisplayedColumns().map((col: Column) => col.getColDef())
);

export default IonInfiniteScrollGrid;
